/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : LoginGainsBrand.tsx
// 설명   : 로그인 화면 좌측(브랜드) 패널 - 사용자가 만든 GAINS 소개 아티팩트(claude.ai/artifact/RLvf2wkSiz2E2q5Zot9DZR)의
//          본문을 그대로 옮겨온 것(2026-09-28). 헤더/히어로/Who gains/기능/영업흐름/이름 이야기/도입문의까지
//          원문 구성·카피를 그대로 유지하고, 스타일만 이 화면 안에서만 적용되도록 .gl-root로 스코프했음
//          (styles/loginGainsBrand.css). 실제 로그인 입력은 이 패널이 아니라 우측 폼(LoginPage.tsx의
//          lp-form-panel)에 그대로 있고, 이 패널의 "도입 상담 신청" 폼은 원본 그대로의 미리보기용 폼(실제 전송 없음).
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useEffect, useRef, useState } from 'react';
import '../styles/loginGainsBrand.css';

const LoginGainsBrand: React.FC = () => {
  // 원본 아티팩트가 쓰던 구글폰트(Bricolage Grotesque/IBM Plex Sans KR/IBM Plex Mono)를 이 패널을 쓸 때만 head에 추가
  useEffect(() => {
    const added: HTMLLinkElement[] = [];
    const add = (rel: string, href: string, crossOrigin?: boolean) => {
      const l = document.createElement('link');
      l.rel = rel; l.href = href;
      if (crossOrigin) l.crossOrigin = 'anonymous';
      document.head.appendChild(l);
      added.push(l);
    };
    add('preconnect', 'https://fonts.googleapis.com');
    add('preconnect', 'https://fonts.gstatic.com', true);
    add('stylesheet', 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,700;12..96,800&family=IBM+Plex+Sans+KR:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
    return () => { added.forEach(l => l.remove()); };
  }, []);

  const companyRef = useRef<HTMLInputElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const [formMsg, setFormMsg] = useState('');

  // 원본 아티팩트의 인라인 스크립트(필수값 검사 후 안내문구 표시, 실제 전송 없음)를 그대로 재현
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const missing = [companyRef, nameRef, phoneRef].find(r => !r.current?.value.trim());
    if (missing) {
      setFormMsg('회사명, 담당자, 연락처를 모두 입력해 주세요.');
      missing.current?.focus();
      return;
    }
    setFormMsg('홍보용 미리보기 페이지라 신청 내용은 아직 전송되지 않습니다. 서버를 연결하면 접수됩니다.');
  };

  return (
    <div className="gl-root">
      <header className="top">
        <div className="wrap">
          <a className="logo" href="#gl-top" aria-label="GAINS 홈">G<b>AI</b>NS</a>
          <nav className="nav" aria-label="주요 메뉴">
            <a href="#gl-who">도입 효과</a>
            <a href="#gl-features">기능</a>
            <a href="#gl-name">GAINS 이야기</a>
            <a className="btn btn-primary" href="#gl-contact">도입 문의</a>
          </nav>
        </div>
      </header>

      <main id="gl-top">
        <section className="hero">
          <div className="wrap hero-grid">
            <div>
              <p className="eyebrow">법인보험대리점(GA)을 위한 AI 영업·관리 플랫폼</p>

              <div className="equation" aria-label="GAINS = GA + AI + Insurance">
                <span className="ch">G</span>
                <span className="ch ai first">A</span>
                <span className="ch ai last">I</span>
                <span className="ch">N</span>
                <span className="ch">S</span>
                <span className="brace ga"><b>GA</b> 보험대리점</span>
                <span className="brace ai"><b>AI</b> 인공지능</span>
                <span className="brace ins"><b>INS</b>urance 보험</span>
              </div>

              <h1>GA와 AI가 만나면, <em>모두가 얻는다</em></h1>
              <p className="lede">계약관리, 예정수수료, 수수료 정산, 시책 정산까지. 여러 보험사를 다루는 GA의 복잡다양한 업무를 AI와 함께 하나의 시스템에서 처리합니다.</p>
              <div className="ctas">
                <a className="btn btn-primary" href="#gl-contact">도입 상담 신청</a>
                <a className="btn btn-ghost" href="#gl-features">기능 살펴보기</a>
              </div>
            </div>

            <div className="mock" role="img" aria-label="GAINS AI 보장분석 화면 예시">
              <div className="mock-head">
                <strong>AI 보장분석</strong>
                <span className="tag">예시 화면</span>
              </div>
              <div className="client">
                <div className="avatar" aria-hidden="true">김</div>
                <div>
                  <strong>김○○ 고객</strong>
                  <small>42세 · 기존 계약 4건 · 3개 보험사</small>
                </div>
              </div>
              <div className="legend">
                <span><i /> 현재 보장</span>
                <span><i className="t" /> 권장 수준</span>
              </div>
              <ul className="cov">
                <li><span>일반사망</span><div className="bar"><i style={{ width: '48%' }} /><u style={{ left: '70%' }} /></div><span className="chip low">부족</span></li>
                <li><span>암 진단</span><div className="bar"><i style={{ width: '62%' }} /><u style={{ left: '65%' }} /></div><span className="chip ok">적정</span></li>
                <li><span>뇌·심혈관</span><div className="bar"><i style={{ width: '22%' }} /><u style={{ left: '60%' }} /></div><span className="chip low">부족</span></li>
                <li><span>실손의료비</span><div className="bar"><i style={{ width: '92%' }} /><u style={{ left: '50%' }} /></div><span className="chip dup">중복</span></li>
                <li><span>입원일당</span><div className="bar"><i style={{ width: '55%' }} /><u style={{ left: '55%' }} /></div><span className="chip ok">적정</span></li>
              </ul>
              <div className="ai-note">
                <span className="who">GAINS AI 제안</span>
                <span>뇌·심혈관 보장이 권장 수준보다 크게 부족합니다. 실손 <b>중복 가입 1건</b> 정리 후 <b>5개 보험사 비교안 3건</b>을 준비했습니다.</span>
              </div>
            </div>
          </div>
        </section>

        <section className="block" id="gl-who">
          <div className="wrap">
            <div className="sec-head">
              <p className="eyebrow">Who gains</p>
              <h2>설계사, GA 본사 모두가 얻습니다</h2>
              <p>GAINS라는 이름처럼, 한쪽만 좋아지는 시스템이 아닙니다.</p>
            </div>
            <div className="gains-for">
              <div>
                <h3><span>PLANNER</span>설계사</h3>
                <ul>
                  <li>상담 준비 시간을 줄이는 AI 설계 초안</li>
                  <li>수십 개 보험사 상품을 한 화면에서 비교</li>
                  <li>청약 전 완전판매 체크리스트 자동 점검</li>
                </ul>
              </div>
              <div>
                <h3><span>GA HQ</span>GA 본사</h3>
                <ul>
                  <li>조직별 실적과 유지율을 실시간으로 파악</li>
                  <li>수수료 분급·환수 정산과 1200% 규제 검증</li>
                  <li>불완전판매 위험 계약을 사전에 알림</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="block" id="gl-features">
          <div className="wrap">
            <div className="sec-head">
              <p className="eyebrow">Features</p>
              <h2>GA 업무에 맞춰 만든 기능</h2>
              <p>일반 CRM이 아닌, 여러 보험사를 다루는 GA의 업무 방식을 기준으로 설계했습니다.</p>
            </div>
            <div className="features">
              <div className="feat">
                <span className="k">CONTRACT</span>
                <h3>계약관리</h3>
                <p>계약 등록부터 상태 변경, 증권정보까지 보험사별 계약 현황을 한 화면에서 관리합니다.</p>
              </div>
              <div className="feat">
                <span className="k">FORECAST</span>
                <h3>예정수수료관리</h3>
                <p>회차별 예정수수료를 미리 계산해 실제 정산 전에 예상 수입을 파악합니다.</p>
              </div>
              <div className="feat">
                <span className="k">SETTLEMENT</span>
                <h3>수수료정산</h3>
                <p>보험사별 수수료 분급과 환수를 자동으로 계산하고, 1200% 규제 한도를 넘지 않는지 검증합니다.</p>
              </div>
              <div className="feat">
                <span className="k">INCENTIVE</span>
                <h3>시책정산</h3>
                <p>설계사별 시책 달성 실적을 집계해 지급액을 자동으로 산정합니다.</p>
              </div>
              <div className="feat">
                <span className="k">PAYMENT</span>
                <h3>지급정산</h3>
                <p>수수료와 시책을 합산해 설계사에게 지급할 최종 금액을 정산합니다.</p>
              </div>
              <div className="feat">
                <span className="k">RETENTION</span>
                <h3>유지율관리</h3>
                <p>13회차·25회차 유지율을 설계사와 조직별로 추적하고, 실효 위험 계약을 미리 알려줍니다.</p>
              </div>
              <div className="feat">
                <span className="k">DASHBOARD</span>
                <h3>대시보드</h3>
                <p>지점, 팀, 설계사별 실적과 목표 달성률을 본사와 현장이 같은 숫자로 확인합니다.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="block" id="gl-name">
          <div className="wrap story">
            <div>
              <p className="eyebrow">Why GAINS</p>
              <p className="formula">
                GA <span className="op">+</span> <mark>AI</mark> <span className="op">+</span> INSurance<br />
                <span className="op">=</span> G<mark>AI</mark>NS
              </p>
            </div>
            <dl className="readings">
              <div><dt>겹치는 글자</dt><dd>GA와 AI는 A를, AI와 INSurance는 I를 함께 씁니다. GA의 영업 경험, AI의 분석, 보험이 서로 맞물려 하나가 된다는 뜻입니다.</dd></div>
              <div><dt>INSurance</dt><dd>이름 안에 GA와 보험이 함께 들어 있습니다. GAINS는 보험 GA를 위해 만든 시스템입니다.</dd></div>
              <div><dt>gains</dt><dd>얻다. 고객은 더 나은 보장을, 설계사는 더 많은 시간을, GA는 더 건강한 조직을 얻습니다.</dd></div>
            </dl>
          </div>
        </section>

        <section className="block" id="gl-contact">
          <div className="wrap">
            <div className="cta">
              <div>
                <h2>우리 GA에 GAINS를 도입해 보세요</h2>
                <p>조직 규모와 제휴 보험사 구성에 맞춰 도입 방식을 안내해 드립니다.</p>
                <p className="addr">gains.thepeoplesoft.com</p>
              </div>
              <form onSubmit={handleSubmit} noValidate>
                <div className="row2">
                  <label htmlFor="gl-f-company">회사명<input id="gl-f-company" ref={companyRef} placeholder="○○ 보험대리점" required /></label>
                  <label htmlFor="gl-f-name">담당자<input id="gl-f-name" ref={nameRef} placeholder="이름과 직책" required /></label>
                </div>
                <div className="row2">
                  <label htmlFor="gl-f-phone">연락처<input id="gl-f-phone" ref={phoneRef} type="tel" placeholder="010-0000-0000" required /></label>
                  <label htmlFor="gl-f-size">소속 설계사 수
                    <select id="gl-f-size" defaultValue="100명 미만">
                      <option>100명 미만</option>
                      <option>100 ~ 500명</option>
                      <option>500 ~ 1,000명</option>
                      <option>1,000명 이상</option>
                    </select>
                  </label>
                </div>
                <button className="btn btn-accent" type="submit">도입 상담 신청</button>
                <p className="form-msg" role="status">{formMsg}</p>
              </form>
            </div>
          </div>
        </section>
      </main>

      <footer>
        <div className="wrap">
          <span>© 2026 PEOPLESOFT · GAINS</span>
          <span>GA + AI + INSurance</span>
        </div>
      </footer>
    </div>
  );
};

export default LoginGainsBrand;
